// Nutzungs-Check der Payment-Seite. Vergleicht die heutige Nutzung des Kunden
// mit der Zahlung im Formular. Hinweise zu Stellen, Zugängen und
// Talentspeicher blockieren nie. Blockierend ist nur die DSGVO-Bestätigung,
// die die Seite über dsgvo übergibt. Ob die Grenzen ab dem Speichern gelten,
// hängt am Status der Pricing-Sperre für diesen Kunden (enforcement).
import { useMemo } from 'react'
import {
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/20/solid'

import { CUSTOM_PLAN, normalizePlanId } from '../../constants/pricing-catalog'
import { classNames } from '../../helper'
import {
  PLAN_NAMES,
  effectiveLimitsOf,
  formatCount,
  planAppliesImmediately,
  recommendPlan
} from '../../helper/pricing'
import {
  LIMIT_STATES,
  resolveLimitState
} from '../../helper/pricing-mirror/limits'
import { ENFORCEMENT_STATES } from '../../helper/pricing-flag'
import { usePricingUsage } from '../../hooks/use-pricing-usage'
import UsageMeter from './UsageMeter'

export const FINDING_KINDS = { WARNING: 'warning', INFO: 'info' }

// Sprungziele für die Fehlerübersicht der Payment-Seite
export const DSGVO_WARNING_ID = 'dsgvo-warning'
export const DSGVO_CONFIRM_ID = 'dsgvo-confirm'

// Zusätzlicher Zustand, solange FEATURE_FLAGS lädt
export const ENFORCEMENT_LOADING = 'loading'

// Sperre für diesen Kunden an, die Grenzen gelten ab dem Speichern
export const isEnforcedState = (enforcement) =>
  enforcement === ENFORCEMENT_STATES.ACTIVE ||
  enforcement === ENFORCEMENT_STATES.WAITING

/**
 * Satz zum Status der Pricing-Sperre unter dem Nutzungs-Check.
 *
 * @param {{ enforcement: string, appliesNow?: boolean }} params enforcement
 *   aus pricingEnforcementState für den Stand nach dem Speichern oder
 *   ENFORCEMENT_LOADING
 */
export const enforcementNote = ({ enforcement, appliesNow = false }) => {
  if (isEnforcedState(enforcement)) {
    return 'Die Pricing-Sperre ist für diesen Kunden an. Alle Grenzen gelten ab dem Speichern.'
  }
  if (
    enforcement === ENFORCEMENT_STATES.READY ||
    enforcement === ENFORCEMENT_STATES.OFF
  ) {
    return appliesNow
      ? 'Die Grenzen für Stellen und Talentspeicher gelten erst, wenn die Pricing-Sperre für diesen Kunden an ist. Heute ist sie aus.'
      : 'Die Grenzen gelten erst, wenn die Pricing-Sperre für diesen Kunden an ist. Heute ist sie aus, bis dahin rechnet das Dashboard mit dem Altvertrag.'
  }
  if (enforcement === ENFORCEMENT_STATES.PARTIAL) {
    return 'Teil-Rollout. Ob die Pricing-Sperre für diesen Kunden an ist, rechnet das OS nicht nach. Rechne damit, dass die Grenzen ab dem Speichern gelten.'
  }
  if (enforcement === ENFORCEMENT_LOADING) {
    return 'Status der Pricing-Sperre wird geladen.'
  }
  if (enforcement === ENFORCEMENT_STATES.PLAN_UNKNOWN) {
    return 'Status der Pricing-Sperre unbekannt, die Schema-Prüfung ist fehlgeschlagen und Pakete sind nicht lesbar. Rechne damit, dass die Grenzen ab dem Speichern gelten.'
  }
  return 'Status der Pricing-Sperre unbekannt, FEATURE_FLAGS ist im OS nicht lesbar. Rechne damit, dass die Grenzen ab dem Speichern gelten.'
}

const count = (n, one, other) => `${formatCount(n)} ${n === 1 ? one : other}`

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null
  const n = Number(value)
  return Number.isNaN(n) ? null : n
}

/**
 * Die Zahlung im Formular als Payment für die Rechenregeln. Mit id ersetzt
 * sie beim Bearbeiten das gespeicherte Payment.
 */
export const draftPaymentOf = ({ values, planId, paymentId, createdAt }) => ({
  ...(paymentId ? { id: paymentId } : {}),
  ...(createdAt ? { createdAt } : {}),
  plan: planId || null,
  is_trial: planId ? false : !!values?.is_trial,
  products: values?.products || [],
  career_count: toNumberOrNull(values?.career_count),
  seat_count: toNumberOrNull(values?.seat_count),
  spaces_count: toNumberOrNull(values?.spaces_count)
})

/**
 * Hinweise aus Nutzung und Limits. Nie blockierend. Mit enforced (Sperre für
 * diesen Kunden an) gelten die Grenzen ab dem Speichern, sonst erst mit
 * aktiver Sperre.
 *
 * @param {{ usage: object|null, limits: { careers, seats, talentStorage },
 *   planId: string, appliesNow: boolean, enforced?: boolean }} params usage
 *   aus usePricingUsage, limits aus effectiveLimitsOf
 * @returns {{ kind: 'warning'|'info', resource: string, message: string }[]}
 */
export const usageFindings = ({
  usage,
  limits,
  planId,
  appliesNow = false,
  enforced = false
}) => {
  if (!usage || !limits) return []

  const subject = planId === CUSTOM_PLAN ? 'die Zahlung' : 'das Paket'
  const findings = []
  const push = (kind, resource, message) =>
    findings.push({ kind, resource, message })

  const careers = resolveLimitState({
    resource: 'careers',
    limit: limits.careers,
    used: usage.careers
  })
  const careersHead = `${count(careers.used, 'aktive Stelle', 'aktive Stellen')}, ${subject} erlaubt ${formatCount(careers.limit)}.`
  if (careers.state === LIMIT_STATES.OVER_LIMIT) {
    push(
      FINDING_KINDS.WARNING,
      'careers',
      enforced
        ? `${careersHead} Ab dem Speichern kann der Kunde erst wieder Stellen aktivieren, wenn er unter dem Limit ist. Archiviert wird nichts.`
        : `${careersHead} Mit aktiver Pricing-Sperre kann der Kunde erst wieder Stellen aktivieren, wenn er unter dem Limit ist. Archiviert wird nichts.`
    )
  } else if (careers.state === LIMIT_STATES.AT_LIMIT) {
    push(
      FINDING_KINDS.INFO,
      'careers',
      enforced
        ? `${careersHead} Ab dem Speichern lässt sich keine weitere Stelle aktivieren.`
        : `${careersHead} Mit aktiver Pricing-Sperre lässt sich keine weitere Stelle aktivieren.`
    )
  }

  const storage = resolveLimitState({
    resource: 'talentStorage',
    limit: limits.talentStorage,
    used: usage.talentStorage
  })
  const locked =
    typeof usage.lockedTalents === 'function'
      ? usage.lockedTalents(limits.talentStorage)
      : 0
  if (locked > 0) {
    const storageHead = `${count(storage.used, 'Talent', 'Talente')} im Speicher, ${subject} erlaubt ${formatCount(storage.limit)}.`
    push(
      FINDING_KINDS.WARNING,
      'talentStorage',
      enforced
        ? `${storageHead} ${count(locked, 'Talent wird', 'Talente werden')} ab dem Speichern gesperrt, Score und Report sind dann verdeckt. Platz wird nur durch Löschen frei.`
        : `${storageHead} ${count(locked, 'Talent wäre', 'Talente wären')} gesperrt, Score und Report sind dann verdeckt. Platz wird nur durch Löschen frei.`
    )
  } else if (storage.state === LIMIT_STATES.AT_LIMIT) {
    push(
      FINDING_KINDS.INFO,
      'talentStorage',
      enforced
        ? 'Der Talentspeicher ist voll. Neue Talente werden ab dem Speichern gesperrt.'
        : 'Der Talentspeicher ist voll. Mit aktiver Pricing-Sperre wären neue Talente gesperrt.'
    )
  }

  const seats = resolveLimitState({
    resource: 'seats',
    limit: limits.seats,
    used: usage.seats
  })
  const seatsFull =
    seats.state === LIMIT_STATES.OVER_LIMIT ||
    seats.state === LIMIT_STATES.AT_LIMIT
  if (seatsFull && (appliesNow || enforced)) {
    push(
      FINDING_KINDS.WARNING,
      'seats',
      appliesNow
        ? `${count(seats.used, 'Zugang', 'Zugänge')}, diese Zahlung erlaubt ${formatCount(seats.limit)}. Das gilt sofort nach dem Speichern, auch ohne Pricing-Sperre. Niemand wird entfernt, der Kunde kann aber niemanden mehr einladen.`
        : `${count(seats.used, 'Zugang', 'Zugänge')}, ${subject} erlaubt ${formatCount(seats.limit)}. Ab dem Speichern kann der Kunde niemanden mehr einladen. Niemand wird entfernt.`
    )
  } else if (seatsFull) {
    push(
      seats.state === LIMIT_STATES.OVER_LIMIT
        ? FINDING_KINDS.WARNING
        : FINDING_KINDS.INFO,
      'seats',
      `${count(seats.used, 'Zugang', 'Zugänge')}, ${subject} erlaubt ${formatCount(seats.limit)}. Mit aktiver Pricing-Sperre kann der Kunde niemanden mehr einladen. Niemand wird entfernt.`
    )
  }

  return findings
}

/**
 * Paket-Empfehlung zur heutigen Nutzung als Satz.
 */
export const recommendationText = (usage) => {
  if (!usage) return null
  const { planId, addonCareers, addonSeats } = recommendPlan({
    careers: usage.careers,
    seats: usage.seats,
    storage: usage.talentStorage
  })

  const extras = [
    addonCareers > 0 && count(addonCareers, 'Zusatzstelle', 'Zusatzstellen'),
    addonSeats > 0 && count(addonSeats, 'Zusatzzugang', 'Zusatzzugängen')
  ].filter(Boolean)

  return `Passend zur heutigen Nutzung wäre ${PLAN_NAMES[planId]}${
    extras.length ? ` mit ${extras.join(' und ')}` : ''
  }.`
}

const Finding = ({ kind, children }) => {
  const warning = kind === FINDING_KINDS.WARNING
  const Icon = warning ? ExclamationTriangleIcon : InformationCircleIcon

  return (
    <li
      className={classNames(
        'flex items-start gap-x-2 text-sm',
        warning ? 'text-amber-900' : 'text-gray-700'
      )}
    >
      <Icon
        className={classNames(
          'mt-0.5 h-4 w-4 flex-shrink-0',
          warning ? 'text-amber-500' : 'text-gray-400'
        )}
        aria-hidden='true'
      />
      <span>{children}</span>
    </li>
  )
}

const DsgvoBlock = ({ dsgvo, confirmed, onConfirmedChange }) => (
  <div
    className={classNames(
      'rounded-lg border p-4',
      dsgvo.blocking
        ? 'border-red-200 bg-red-50'
        : 'border-amber-200 bg-amber-50'
    )}
    id={DSGVO_WARNING_ID}
    data-testid='dsgvo-warning'
    tabIndex={-1}
  >
    <div className='flex items-start gap-x-2'>
      <ExclamationTriangleIcon
        className={classNames(
          'mt-0.5 h-5 w-5 flex-shrink-0',
          dsgvo.blocking ? 'text-red-500' : 'text-amber-500'
        )}
        aria-hidden='true'
      />
      <div>
        <h3
          className={classNames(
            'text-sm font-semibold',
            dsgvo.blocking ? 'text-red-900' : 'text-amber-900'
          )}
        >
          DSGVO-Automatisierung
        </h3>
        <p
          className={classNames(
            'mt-1 text-sm',
            dsgvo.blocking ? 'text-red-800' : 'text-amber-800'
          )}
        >
          {dsgvo.message}
        </p>
        {dsgvo.blocking && (
          <label className='mt-3 flex items-start gap-x-2 text-sm font-medium text-red-900'>
            <input
              id={DSGVO_CONFIRM_ID}
              type='checkbox'
              className='mt-0.5 h-4 w-4 rounded border-red-300 text-red-600 focus:ring-red-600'
              checked={!!confirmed}
              onChange={(event) => onConfirmedChange(event.target.checked)}
            />
            <span>
              Geprüft. Ich speichere bewusst ohne DSGVO-Automatisierung.
            </span>
          </label>
        )}
      </div>
    </div>
  </div>
)

/**
 * @param {object} props
 * @param {string} props.partnerId
 * @param {string|null} props.planId Paket der Zahlung im Formular, null ohne
 *   Paket (dann nur der DSGVO-Teil)
 * @param {object} props.values Formularwerte
 * @param {object[]} [props.payments] alle Payments des Kunden
 * @param {string} [props.paymentId] ID der bearbeiteten Zahlung
 * @param {string} [props.createdAt] createdAt der bearbeiteten Zahlung
 * @param {{ blocking: boolean, message: string }|null} [props.dsgvo]
 * @param {boolean} [props.dsgvoConfirmed]
 * @param {(confirmed: boolean) => void} [props.onDsgvoConfirmedChange]
 * @param {string} [props.enforcement] Status der Pricing-Sperre nach dem
 *   Speichern (pricingEnforcementState) oder ENFORCEMENT_LOADING. Ohne Angabe
 *   gilt unknown.
 */
const UsageCheck = ({
  partnerId,
  planId,
  values,
  payments = [],
  paymentId,
  createdAt,
  enforcement = ENFORCEMENT_STATES.UNKNOWN,
  dsgvo = null,
  dsgvoConfirmed = false,
  onDsgvoConfirmedChange = () => undefined
}) => {
  const target = normalizePlanId(planId)
  const showUsage = !!target

  const { usage, isLoading, isError, isFetching, refetch } = usePricingUsage(
    partnerId,
    { enabled: showUsage }
  )

  const draft = useMemo(
    () => draftPaymentOf({ values, planId: target, paymentId, createdAt }),
    [values, target, paymentId, createdAt]
  )
  const limits = useMemo(() => effectiveLimitsOf(draft), [draft])
  const { appliesNow, messages } = useMemo(
    () => planAppliesImmediately({ payments, draft }),
    [payments, draft]
  )
  const enforced = isEnforcedState(enforcement)
  const findings = useMemo(
    () =>
      usageFindings({ usage, limits, planId: target, appliesNow, enforced }),
    [usage, limits, target, appliesNow, enforced]
  )
  const recommendation = useMemo(() => recommendationText(usage), [usage])

  if (!showUsage && !dsgvo) return null

  const failed = isError && !usage

  return (
    <div className='space-y-4'>
      {showUsage && (
        <div className='rounded-lg border border-gray-200 bg-white p-4'>
          <div className='flex flex-wrap items-start justify-between gap-3'>
            <div>
              <h3 className='text-sm font-semibold text-gray-900'>
                Nutzungs-Check
              </h3>
              <p className='mt-1 text-sm text-gray-600'>
                Heutige Nutzung des Kunden gegen diese Zahlung, gezählt wie im
                Dashboard.
              </p>
            </div>
            {failed && (
              <button
                type='button'
                onClick={() => refetch()}
                disabled={isFetching}
                className='rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-40'
              >
                Erneut versuchen
              </button>
            )}
          </div>

          {failed && (
            <p className='mt-3 text-sm text-red-700'>
              Nutzung konnte nicht geladen werden. Speichern geht trotzdem.
            </p>
          )}

          <div className='mt-4 grid grid-cols-1 gap-6 sm:grid-cols-3'>
            <UsageMeter
              label='Gleichzeitig offene Stellen'
              used={usage ? usage.careers : null}
              limit={limits.careers}
              failed={failed}
            />
            <UsageMeter
              label='Dashboard-Zugänge'
              used={usage ? usage.seats : null}
              limit={limits.seats}
              failed={failed}
            />
            <UsageMeter
              label='Talentspeicher'
              used={usage ? usage.talentStorage : null}
              limit={limits.talentStorage}
              lockedTalents={
                usage ? usage.lockedTalents(limits.talentStorage) : 0
              }
              failed={failed}
            />
          </div>

          {isLoading && (
            <p className='mt-3 text-sm text-gray-500'>Nutzung wird gezählt</p>
          )}

          {findings.length > 0 && (
            <ul className='mt-4 space-y-2'>
              {findings.map(({ kind, resource, message }) => (
                <Finding key={resource} kind={kind}>
                  {message}
                </Finding>
              ))}
            </ul>
          )}

          {appliesNow && (
            <div className='mt-4 rounded-md bg-indigo-50 p-3 text-sm text-indigo-900'>
              {messages.map((message) => (
                <p key={message} className='mt-1 first:mt-0'>
                  {message}
                </p>
              ))}
            </div>
          )}
          <p
            className={classNames(
              'mt-4 text-sm',
              enforced ? 'font-medium text-amber-900' : 'text-gray-500'
            )}
            data-testid='enforcement-note'
          >
            {enforcementNote({ enforcement, appliesNow })}
          </p>

          {recommendation && (
            <p className='mt-2 text-sm text-gray-500'>{recommendation}</p>
          )}
        </div>
      )}

      {dsgvo && (
        <DsgvoBlock
          dsgvo={dsgvo}
          confirmed={dsgvoConfirmed}
          onConfirmedChange={onDsgvoConfirmedChange}
        />
      )}
    </div>
  )
}

export default UsageCheck
